'use client'

import { useState, useCallback } from 'react'
import { Link2, Check } from 'lucide-react'

interface ShareWidgetProps {
  /** Article title (reserved for future use) */
  title: string
  /** URL to copy (defaults to current page URL) */
  url?: string
}

export default function ShareWidget({ title, url }: ShareWidgetProps) {
  const [copied, setCopied] = useState(false)

  const copyToClipboard = useCallback(async () => {
    const urlToCopy = url ?? window.location.href
    try {
      await navigator.clipboard.writeText(urlToCopy)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea')
      textarea.value = urlToCopy
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [url])

  return (
    <button
      onClick={copyToClipboard}
      className="inline-flex items-center gap-2 rounded-full cursor-pointer"
      style={{
        padding: '0.5rem 1rem',
        fontSize: '0.8125rem',
        fontWeight: 500,
        color: copied ? '#22C55E' : '#A1A1AA',
        background: 'linear-gradient(135deg, rgba(18, 18, 18, 0.8) 0%, rgba(10, 10, 10, 0.7) 100%)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        transition: 'color 200ms ease, border-color 200ms ease',
      }}
      onMouseEnter={(e) => {
        if (!copied) e.currentTarget.style.color = '#fff'
        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.12)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = copied ? '#22C55E' : '#A1A1AA'
        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.06)'
      }}
      aria-label={copied ? 'Link copied' : 'Copy link'}
    >
      <span
        className="flex items-center gap-1.5"
        style={{
          transition: 'transform 0.2s ease, opacity 0.2s ease',
        }}
      >
        {copied ? (
          <>
            <Check size={14} />
            Copied!
          </>
        ) : (
          <>
            <Link2 size={14} />
            Copy Link
          </>
        )}
      </span>
    </button>
  )
}
